// Import the headless editor integration hook
import { useTtfeEditorTracking } from '@src/analytics/hooks/useTtfeEditorTracking';
import { useShortcutScope } from '@src/hooks/shortcuts/useShortcutScope';
import { useCorrectedAudioHotSwap } from '@src/hooks/useCorrectedAudioHotSwap';
import { useHeadlessEditorLogging } from '@src/hooks/useHeadlessEditorLogging';
import { useUpdateSearchParams } from '@src/hooks/useUpdateSearchParams';
import SaveStoryAndVideoService, {
  createVideoCreatorSaveAdapter,
} from '@src/services/SaveStoryAndVideoService';
import { createGlobalShortcutScope } from '@src/services/shortcuts/config/globalShortcuts';
import {
  useDatoClient,
  useStore,
  useTemplatedPrompts,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useGesture } from '@use-gesture/react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { STOCK_MUSIC_QUERY } from '../gql/media-gql';
import {
  requiresNativeLocalPreview,
  waitForRenditionFetchable,
} from '../services/LocalCanvasPreview';
import { WarmedRenderer } from '../services/WarmedRenderer';
import { pickRenditionUrl } from '../utility/clipMediaSource';
import { request } from '../utility/dato';
import { inDebugMode } from '../utility/debug';
import { extractErrorInfo } from '../utility/error';
import { requestMissingFields } from '../utility/story';
import { isCompressedRenditionPending } from '../utility/storyReadiness';
import { useFlagsCombination } from '../utility/useFlagsCombination';
import ClientReadyFlag from './common/ClientReadyFlag';
import ErrorBoundary from './common/ErrorBoundary';
import GlobalGeneratedPunchListModal from './common/GlobalGeneratedPunchListModal';
import { PlayerSettingsSlotProvider } from './common/playerSettingsSlot';
import PreviewProcessingOverlay from './common/PreviewProcessingOverlay';
import ReplacePhotoElementModal from './common/ReplacePhotoElementModal';
import StatusMessageModal from './common/StatusMessageModal';
import NewVideoVersionListener from './NewVideoVersionListener';
import { LeftSidePanel } from './sidepanel/LeftSidePanel';
import SpeakerIdentificationPrompt from './sidepanel/modals/SpeakerIdentificationPrompt';
import { RightSidePanel } from './sidepanel/RightSidePanel';
import { BackToContentStudioButton } from './stage/BackToContentStudioButton';
import { CreateButton } from './stage/CreateButton';
import { DownloadButton } from './stage/DownloadButton';
import { PublishVoiceNotice } from './stage/PublishVoiceNotice';
import RetryPublishModal from './stage/RetryPublishModal';
import { SaveButton } from './stage/SaveButton';
import { ShortcutsModal } from './stage/ShortcutsModal';
import { Stage } from './stage/Stage';
import { VideoSelector } from './stage/VideoSelector';
import { Timeline } from './timeline/Timeline';

import type { Music } from '../types.ts/story';

const logger = LogManager.getLogger('component:VideoCreator');

export const VideoCreator: React.FC<{}> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const rootStore = useStore();
  const uploadFlowStore = useUserStoryUploadFlow();
  const templatedPromptsStore = useTemplatedPrompts();
  const analyticsStore = rootStore.analyticsStore;
  const datoClientStore = useDatoClient();
  const userIdentity = useUserIdentity();
  const story = videoCreator.story;
  const { enableGenerateAiPhoto, appEnableApprovedButton } =
    useFlagsCombination(userIdentity.currentRole);
  const [searchParams, setSearchParams] = useSearchParams();
  const updateSearchParams = useUpdateSearchParams();
  const { storyId, videoId, showcase } = Object.fromEntries(
    searchParams.entries(),
  );

  // Initialize headless editor integration with VideoCreatorStore
  // Wire the headless editor to VideoSourceStore. Debug tools toggle follows
  // the existing debug flag.
  useHeadlessEditorLogging(inDebugMode());

  // TTFE (PLAT-11223): first_edit_accepted + ttc.canvas_ready emitters
  useTtfeEditorTracking();

  // PLAT-11226: swap in Auphonic-corrected audio + waveform when the
  // audio_corrected signal fires (deferred to the next pause/idle).
  useCorrectedAudioHotSwap();

  const ref = useRef<HTMLDivElement>(null);
  useGesture(
    {
      onPinch: () => {},
      onWheel: () => {},
    },
    { target: ref, eventOptions: { passive: false } },
  );

  // PLAT-11680: gates the clip_editor_usable_creator timer. Declared before
  // the initializeData effect so a cold /creator load is timed.
  useEffect(() => {
    videoCreator.setCreatorStudioMounted(true);
    return () => videoCreator.setCreatorStudioMounted(false);
  }, []);

  useEffect(() => {
    videoCreator.setAiImageFeatureFlag(enableGenerateAiPhoto);
  }, [story?.id, enableGenerateAiPhoto]);

  useEffect(() => {
    videoCreator.justLoaded = true;

    // PLAT-11898 — via `updateSearchParams`, not this render's `searchParams`:
    // rewriting the whole query from that copy dropped a `lang` written since.
    if (
      videoCreator.currentVideo &&
      !videoCreator.currentVideo?.id &&
      searchParams.get('videoId')
    ) {
      updateSearchParams(params => params.delete('videoId'));
    } else if (videoCreator.currentVideo?.id) {
      // Right after a story switch (auto-nav from an upload, story picker)
      // the store still holds the PREVIOUS story's video for a few renders.
      // Writing that id into the new story's URL leaks it into the init
      // effect below and survives refreshes — only sync the param once the
      // loaded story matches the URL.
      const urlStoryId = searchParams.get('storyId');
      if (!urlStoryId || videoCreator.story?.id === urlStoryId) {
        const videoId = videoCreator.currentVideo.id;
        updateSearchParams(params => params.set('videoId', videoId));
      }
    }

    // Log debug information if in debug mode
    if (inDebugMode()) {
      logger.debug('Debug mode is ON.');
    }
  }, [videoCreator.currentVideo?.id]);

  // URL story ≠ loaded story: a switch is in flight (or about to be).
  const storySwitching = !!storyId && videoCreator.story?.id !== storyId;

  const shouldInitializeWithNewData = () => {
    const isStoryChanged = storyId && videoCreator.story?.id !== storyId;
    const isVideoChanged = videoId && videoCreator.currentVideo?.id !== videoId;
    const isVideoMissing = storyId && !videoId && !videoCreator.currentVideo;
    // PLAT-11228: while renditions are pending, the freshly autosaved
    // audio-first video landing in the URL must not trigger a re-init — it
    // reloads the saved (stale) composition over the live one and drops the
    // audio track. The story-change and no-video cases still re-init.
    if (
      isVideoChanged &&
      !isStoryChanged &&
      videoCreator.story &&
      isCompressedRenditionPending(videoCreator.story)
    ) {
      // PLAT-11228 kept this from re-initializing when the freshly autosaved
      // audio-first draft landed in the URL (it reloaded the saved, stale
      // composition over the live one). That race is recognizable: the live
      // draft has no id yet, so the URL's videoId can only be that draft.
      // PLAT-11445: any other videoId change while pending is a real switch
      // (a clip opened from Content Studio) and must load.
      const draftLanding =
        !!videoCreator.currentVideo && !videoCreator.currentVideo.id;
      if (draftLanding) {
        logger.debug('Skipping videoId re-init: draft autosave landing', {
          videoId,
        });
        return isVideoMissing;
      }
      logger.debug('Switching video while renditions pending', {
        from: videoCreator.currentVideo?.id,
        to: videoId,
      });
    }
    return isStoryChanged || isVideoChanged || isVideoMissing;
  };

  // PLAT-11911: a clip kept from the last visit (sidebar, "Edit Video") may
  // have changed in Dato since. Mount only: later videoId changes are loads.
  useEffect(() => {
    if (!shouldInitializeWithNewData() && videoCreator.currentVideo) {
      void videoCreator.reconcileLoadedVideoWithHead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // One-shot flag set by Content Studio's "Open full video in Creator
    // Studio": duplicate the original into a fresh editable video on init.
    const createFromOriginal = searchParams.get('newFromOriginal') === 'true';
    if (shouldInitializeWithNewData()) {
      videoCreator.startClipEditorUsableTimerEarly(videoId, storyId);
      videoCreator
        .initializeData({
          storyId,
          videoId,
          showcaseSlug: showcase,
          fromCache: true,
          createFromOriginal,
        })
        // A load inside the init adopts the early timer; later loads mustn't.
        .finally(() => videoCreator.clearClipEditorUsableEarlyStart());
    }
    // Consume the flag so a refresh doesn't duplicate the original again.
    if (createFromOriginal) {
      searchParams.delete('newFromOriginal');
      setSearchParams(searchParams, { replace: true });
    }
    analyticsStore.setContext(storyId, showcase || 'Unknown');
    templatedPromptsStore.initTemplatedPrompts(storyId);
  }, [storyId, videoId, showcase]);

  useEffect(() => {
    (async () => {
      // TODO: Move to Repository
      const stockMusicResponse = (await request({
        query: STOCK_MUSIC_QUERY,
        variables: undefined,
        environment: datoClientStore.environment,
      })) as { allMusics: Music[] };

      videoCreator.stockMusic = stockMusicResponse.allMusics;
    })();
  }, [story?.id]);

  useEffect(() => {
    if (story?.id && videoCreator.isInitialized) {
      (async () => {
        try {
          await videoCreator.loadTranscriptIfNeeded();
        } catch (error) {
          logger.warn('Failed to load transcript in Creator Studio', {
            error: extractErrorInfo(error),
          });
        }
      })();
    }
  }, [story?.id, videoCreator.isInitialized]);

  const saveService = React.useMemo(
    () =>
      new SaveStoryAndVideoService({
        videoCreator: createVideoCreatorSaveAdapter(videoCreator),
      }),
    [videoCreator],
  );

  const globalShortcuts = React.useMemo(() => {
    return createGlobalShortcutScope({
      videoCreator,
      saveService,
    });
  }, [videoCreator, saveService]);

  useShortcutScope(globalShortcuts);

  // PLAT-11224: the canvas is a progressive enhancement — until the 270p
  // preview rendition exists the Stage slot shows a placeholder (the store
  // withholds the unprocessed-original fallback for pending stories), while
  // the transcript and timeline stay mounted and usable.
  // Local unlock latch: the poll flips it once the rendition lands, which
  // guarantees a re-render even if the store's story-patch isn't observed
  // deeply. Reset whenever the story changes.
  const [renditionUnlocked, setRenditionUnlocked] = useState(false);
  useEffect(() => setRenditionUnlocked(false), [story?.id]);

  // Drives the readiness poll: stays true for the whole pending window.
  const renditionPending =
    !!story && !renditionUnlocked && isCompressedRenditionPending(story);
  // Drives the Stage veil only — the local upload playing on the canvas
  // hides the veil but must NOT retire the poll (it is the only thing
  // refetching the story, so killing it would strand the composition on
  // the local source with no rendition handoff).
  const localPreviewFile = story?.id
    ? uploadFlowStore.localVideoFileRecord[story.id]
    : undefined;
  const shouldUseNativeLocalPreview =
    renditionPending &&
    !!localPreviewFile &&
    requiresNativeLocalPreview(localPreviewFile);
  const nativeLocalPreviewUrl =
    renditionPending &&
    (shouldUseNativeLocalPreview ||
      videoCreator.nativeLocalPreviewStoryId === story?.id) &&
    story?.id
      ? uploadFlowStore.getLocalVideoUrl(story.id)
      : null;
  const canvasPending =
    renditionPending &&
    videoCreator.localCanvasStoryId !== story?.id &&
    !nativeLocalPreviewUrl;

  useEffect(() => {
    if (!renditionPending || !story?.id) return undefined;
    let cancelled = false;
    // A tick's async work (fetch + transcript loads + the unlock upgrade) can
    // outlast the 5s cadence — without these guards ticks stack up and the
    // unlock branch runs once per stacked tick, re-initializing the editor
    // over and over (clobbering live edits with the saved snapshot).
    let tickInFlight = false;
    let unlockHandled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const poll = async () => {
      if (tickInFlight || unlockHandled) return;
      tickInFlight = true;
      try {
        const fresh = await datoClientStore.storyRepository.findOne(story.id);
        if (cancelled || !fresh) return;
        // Auto-nav lands here the instant transcription flips ready, which
        // can beat Dato's transcription/waveform URL writes by seconds — the
        // first load attempt fails and nothing retries it. Refresh the
        // transcription metadata and re-attempt both while the story is
        // still pending; each is a no-op once loaded.
        // Patch transcription metadata only while URLs are missing — every
        // fetch re-signs the URLs, and churning them each tick would defeat
        // the path-based element matching downstream. findOne's populate does
        // NOT include the transcription presigns (field census showed
        // hasAudioUrl:false all run), so fetch them the way
        // TranscriptionStore does.
        const knownTranscription = videoCreator.story?.transcription;
        if (
          videoCreator.story &&
          (!knownTranscription?.audio?.url ||
            !knownTranscription?.elementsJson?.url ||
            !knownTranscription?.waveformData?.url)
        ) {
          const { transcription } = await requestMissingFields(story.id, [
            'transcription',
          ]);
          if (cancelled || videoCreator.story?.id !== story.id) return;
          if (transcription && videoCreator.story) {
            videoCreator.story.transcription = transcription;
          }
        }
        if (!videoCreator.rootStore.transcriptionStore.isTranscriptionReady) {
          void videoCreator.loadTranscriptIfNeeded();
        }
        if (!videoCreator.originalWaveForm) {
          void videoCreator.loadOriginalWaveformData();
        }
        // The composition may have been composed before the editor audio
        // URL existed — bootstrap the audio-first element as soon as it
        // arrives so the timeline is editable alongside the transcript.
        const bootstrapped = await videoCreator.ensureAudioFirstComposition();
        if (cancelled || videoCreator.story?.id !== story.id) return;
        logger.debug('Audio-first bootstrap', { bootstrapped });
        // PLAT-11228: when this session still holds the uploaded file, play
        // it on the canvas instead of the audio-only track. Best-effort —
        // every failure path stays on the audio-first canvas.
        await videoCreator.upgradeAudioFirstToLocalCanvas();
        if (cancelled || videoCreator.story?.id !== story.id) return;
        logger.debug('Canvas readiness poll', {
          pending: isCompressedRenditionPending(fresh),
          transcriptionReady:
            videoCreator.rootStore.transcriptionStore.isTranscriptionReady,
        });
        if (!isCompressedRenditionPending(fresh)) {
          // Patch the fields the swap needs and flip the pending flag. The
          // flip retires this poll through its renditionPending dep; the
          // upgrade itself is owned by the rendition-landed effect below —
          // the flag can also be flipped by a store-side story refresh
          // (e.g. a save response), so the swap must not live in this poll.
          unlockHandled = true;
          if (timer) clearInterval(timer);
          if (videoCreator.story) {
            videoCreator.story.originalVideo = fresh.originalVideo;
            videoCreator.story.uploadLog = fresh.uploadLog;
          }
          setRenditionUnlocked(true);
        }
      } catch (error) {
        logger.warn('Canvas readiness poll failed, will retry', {
          error: extractErrorInfo(error),
        });
      } finally {
        tickInFlight = false;
      }
    };
    // Do not add a five-second blind spot at route entry: this first tick can
    // fetch transcription metadata and prepare the audio/local canvas while
    // Creator is mounting. The guarded interval catches renderer readiness and
    // later backend state without stacking async work.
    void poll();
    timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [renditionPending, story?.id, datoClientStore, videoCreator]);

  // Rendition-landed swap. Watches the raw pending flag rather than living
  // in the poll above: the flag can flip through the poll's patch OR any
  // store-side story refresh (a save response, a re-fetch), and whichever
  // path flips it reactively retires the poll — so the poll can't be
  // trusted to run the swap. Only acts when THIS session saw the story
  // pending (the ref), so ordinary post-ready loads don't churn.
  const rawRenditionPending = !!story && isCompressedRenditionPending(story);
  const wasRenditionPendingRef = useRef(false);
  useEffect(() => {
    if (rawRenditionPending) {
      wasRenditionPendingRef.current = true;
      return undefined;
    }
    if (!wasRenditionPendingRef.current || !story?.id) return undefined;
    let cancelled = false;
    let inFlight = false;
    let attempts = 0;
    const tryUpgrade = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        if (videoCreator.isLoading) {
          // A render (edit rebuild, split) is mid-flight — swapping the
          // composition under it corrupts both flows. Retry shortly.
          return;
        }
        if (!videoCreator.story?.transcription?.audio?.url) {
          // Nothing audio-first to preserve — re-init to load the video.
          // Stop the timer only after the re-init lands: a mid-flight throw
          // must leave the retry loop alive (the catch says "will retry",
          // so it has to be true).
          await videoCreator.initializeData({
            storyId: story.id,
            videoId,
            showcaseSlug: showcase,
            fromCache: false,
          });
          wasRenditionPendingRef.current = false;
          clearInterval(timer);
          // The re-init loaded the real video — the locally-held upload
          // (PLAT-11228) has nothing left to serve.
          uploadFlowStore.releaseLocalVideo(story.id);
          return;
        }
        // PLAT-11445: populate lists the rendition before its bytes are
        // readable. Probe through the renderer's own fetch (it also warms
        // the cache, so the swap paints at once) and retry on the timer.
        const renditionUrl = pickRenditionUrl(
          videoCreator.story?.originalVideo?.video,
        );
        const renderer = videoCreator.renderer;
        const warm =
          renderer instanceof WarmedRenderer
            ? (url: string) => renderer.warmAsset(url)
            : null;
        if (renditionUrl && warm) {
          const fetchable = await waitForRenditionFetchable(
            warm,
            renditionUrl,
            { maxAttempts: 1 },
          );
          if (!fetchable) {
            logger.debug('Rendition not fetchable yet', { attempts });
            if (++attempts >= 30) {
              wasRenditionPendingRef.current = false;
              clearInterval(timer);
            }
            return;
          }
        }
        const upgraded = await videoCreator.upgradeAudioFirstComposition();
        logger.debug('Audio-first upgrade result', { upgraded, attempts });
        if (upgraded) {
          // The upgrade swapped the live composition in place and its
          // notify re-pushed it to the renderer — the editor still holds
          // every mid-pending edit. Persist that state, but NEVER re-init
          // from Dato here: a save can fail or queue behind a stuck
          // background save (S3 resource uploads retry for minutes), and a
          // re-init that races it reloads whatever is persisted — often
          // nothing — composing a fresh full-length video over the user's
          // edits. A failed save keeps the live editor intact and surfaces
          // through the existing RetrySavingModal path instead.
          wasRenditionPendingRef.current = false;
          clearInterval(timer);
          // The swap handed the canvas + filmstrip to the real rendition, so
          // the locally-held upload (PLAT-11228) can be released — consumers
          // read null from useLocalPreviewVideoUrl and fall back.
          uploadFlowStore.releaseLocalVideo(story.id);
          await videoCreator.saveStoryAndVideo(false, true, false, true);
          return;
        }
        // Retry while the rendition URLs lag the pending flip (populate
        // lists S3 before the files land); give up after ~90s.
        if (++attempts >= 30) {
          wasRenditionPendingRef.current = false;
          clearInterval(timer);
        }
      } catch (error) {
        logger.warn('Audio-first upgrade attempt failed, will retry', {
          error: extractErrorInfo(error),
        });
        // Same ~90s budget as the URL-lag retries — a persistently failing
        // save/re-init must not spin forever.
        if (++attempts >= 30) {
          wasRenditionPendingRef.current = false;
          clearInterval(timer);
        }
      } finally {
        inFlight = false;
      }
    };
    // Stability delay before the first attempt: an edit issued right at the
    // rendition flip is still rebuilding the composition, and swapping under
    // it corrupts both flows (isLoading alone is a race-prone check). The
    // interval starts after that first delayed attempt and covers retries.
    let timer: ReturnType<typeof setInterval> | undefined;
    // Short stability delay: an edit issued right at the pending flip is
    // still rebuilding the composition. Fetchability is probed per attempt
    // (above), so the long blind wait is gone.
    const firstAttempt = setTimeout(() => {
      void tryUpgrade();
      timer = setInterval(tryUpgrade, 3000);
    }, 3000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      clearTimeout(firstAttempt);
    };
  }, [rawRenditionPending, story?.id, videoCreator, uploadFlowStore]);

  return (
    <ErrorBoundary>
      <Main tabIndex={-1} ref={ref}>
        <CreatorToolbar>
          <ToolbarCenter>
            <BackToContentStudioButton />
            <VideoSelector />
            {appEnableApprovedButton && videoCreator.currentVideo && (
              <ClientReadyFlag video={videoCreator.currentVideo} />
            )}
          </ToolbarCenter>
          <ToolbarRight>
            <ShortcutsModal />
            {/*
             * Which voice Publish will deliver. Creator Studio's publish fans
             * out with the applied caption language, and this surface cannot
             * play that dub, so it says so instead.
             */}
            <PublishVoiceNotice variant="chip" />
            <DownloadButton />
            <SaveButton />
            <CreateButton />
          </ToolbarRight>
        </CreatorToolbar>
        <MainView>
          <PlayerSettingsSlotProvider>
            {!videoCreator.isPlayerFullScreen && (
              <ErrorBoundary>
                <LeftSidePanel />
              </ErrorBoundary>
            )}
            <ErrorBoundary>
              <StageSlot>
                <Stage
                  storyTellerName={story?.storyTeller?.name || ''}
                  hideLoadingIndicator={canvasPending}
                  nativeLocalPreviewUrl={nativeLocalPreviewUrl}
                />
                {canvasPending && (
                  <PreviewProcessingOverlay
                    data-testid="canvas-processing-placeholder"
                    body="The video is still processing — transcript, timeline, and audio playback already work. The canvas unlocks as soon as the preview is ready."
                  />
                )}
              </StageSlot>
            </ErrorBoundary>
            {!videoCreator.isPlayerFullScreen && (
              <ErrorBoundary>
                <RightSidePanel story={story} />
              </ErrorBoundary>
            )}
          </PlayerSettingsSlotProvider>
        </MainView>
        {!videoCreator.isPlayerFullScreen && (
          <ErrorBoundary>
            {/* Until the init effect resets the store, the first render still
                holds the PREVIOUS story — withhold it so the timeline doesn't
                paint that story's tracks for a frame. */}
            <Timeline story={storySwitching ? undefined : story} />
          </ErrorBoundary>
        )}
        {videoCreator.openPhotoElementReplacementModal?.element &&
          (videoCreator.openPhotoElementReplacementModal?.isForPunchlist ? (
            <GlobalGeneratedPunchListModal />
          ) : (
            <ReplacePhotoElementModal />
          ))}
        {videoCreator.renderingStatus === 'error' &&
          videoCreator.renderVideoResLevel &&
          !videoCreator.renderQueueing && (
            <RetryPublishModal
              closeModal={() => {
                videoCreator.renderingStatus = 'none';
                videoCreator.renderVideoResLevel = null;
              }}
            />
          )}

        <StatusMessageModal enabled={false} variant="system-issues" />
        <SpeakerIdentificationPrompt />
        <NewVideoVersionListener />
      </Main>
    </ErrorBoundary>
  );
});

const StageSlot = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
`;

const Main = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  &:focus {
    outline: none;
  }
`;

const CreatorToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 52px;
  min-height: 52px;
  /* The editor's one structural rule — a hairline, never full --border.
     See "Structural Lines" in docs/design-system.md. */
  border-bottom: 1px solid var(--border-subtle);
  background: var(--background);
  position: relative;
  z-index: 100002;
`;

/**
 * Laid out against the left pane's content edge, which sits 37px in — `Panel`'s
 * 5px padding plus `Transcript`'s 32px `Main` margin — and is the line the
 * search icon and the transcript text below share.
 *
 * The back arrow belongs in the margin left of that line and the separator ON
 * it, so the two rows read as one column: 8px in (the -8px cancels part of the
 * toolbar's own 16px padding) + the arrow's 25px box + this 4px gap = 37px.
 * A 28px arrow box at the toolbar's full padding overflowed the margin, which
 * pushed the separator ~30px past the line and the clip's name with it.
 */
const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: -8px;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
`;

const MainView = styled.div`
  flex: 1 1 0%;
  display: flex;
  overflow: hidden;
  position: relative;
  z-index: 100001;
`;
